import moment from 'moment';
import React, { useCallback, useState, useEffect, CSSProperties } from 'react';
import { usePromptsContext } from '../../context/prompts/PromptsContext';
import useInterval from '../../lib/useInterval';
import { useProductUsageQuery } from '../ProductUsage/operation.generated';
import UpgradeBanner from './UpgradeBanner';
import { useUserSubscription } from '../Account/operation.generated';
import { SubscriptionStatus } from '../../../server/graphql/__generated.types';
import { useIsManagedUser } from '../../context/user/UserContext';

interface UpgradeBannerWrapperProps {
  style?: CSSProperties;
}

const UpgradeBannerWrapper = React.forwardRef<
  HTMLElement,
  UpgradeBannerWrapperProps
>(({ style = {} }, ref) => {
  const [, dispatch] = usePromptsContext();
  const isManagedUser = useIsManagedUser();
  const { data } = useProductUsageQuery({
    // Ensure the usage stats are up-to-date
    fetchPolicy: 'cache-and-network',
  });
  const [timeRemainingInMs, setTimeRemaining] = useState<number>();
  const [hasDisplayedUpgradePrompt, setHasDisplayedUpgradePrompt] =
    useState<boolean>(false);
  const { data: subscriptionData } = useUserSubscription();
  const calculateDaysRemaining = useCallback(() => {
    if (!data) return;
    const periodEnd = moment.unix(data.user.productAccessPeriodUsage.periodEnd);
    const now = moment();
    const timeRemaining = periodEnd.diff(now);
    setTimeRemaining(timeRemaining);
  }, [data]);

  // Periodically update this value
  useInterval(calculateDaysRemaining, 60000);

  // Update days remaining whenever product usage changes (and on first load)
  useEffect(calculateDaysRemaining, [data]);

  // Prompt the user to upgrade if they have reached end of their trial
  useEffect(() => {
    if (isManagedUser) {
      return;
    }
    if (
      timeRemainingInMs &&
      timeRemainingInMs < 0 &&
      !hasDisplayedUpgradePrompt
    ) {
      setHasDisplayedUpgradePrompt(true);
      // note: we should probably move this to a shared layout file
      dispatch({
        type: 'OpenSubscriptionTiersPrompt',
        payload: {
          upgradeContext: 'trial_expired',
          upgradePath: 'Trial Expiration Prompt',
        },
      });
    }
  }, [dispatch, hasDisplayedUpgradePrompt, isManagedUser, timeRemainingInMs]);

  const onClick = useCallback(() => {
    if (isManagedUser) {
      return;
    }
    dispatch({
      type: 'OpenSubscriptionTiersPrompt',
      payload: {
        upgradeContext:
          timeRemainingInMs && timeRemainingInMs < 0
            ? 'trial_expired'
            : 'increase_access',
        upgradePath: 'Dashboard > Editor > Upgrade Banner',
      },
    });
  }, [dispatch, isManagedUser, timeRemainingInMs]);

  if (!data) return null;

  // Duration with limited accuracy, for example 13hrs left is rendered as
  const durationLeft = moment.duration(timeRemainingInMs, 'milliseconds');
  let humanizedTimeLeft;
  let leftDays = Math.floor(durationLeft.asDays());
  if (leftDays > 1) {
    if (durationLeft.hours() > 12) leftDays += 1;
    humanizedTimeLeft = `${leftDays} days`;
  } else if (leftDays === 1) {
    humanizedTimeLeft = `${leftDays} day`;
  } else {
    humanizedTimeLeft = moment
      .duration(timeRemainingInMs)
      // @ts-ignore `humanize types need updating: https://github.com/moment/moment/pull/5525
      .humanize(false, { s: 0, m: 0 });
  }

  const takesRemaining = Math.max(
    (data.user.productAccessLimits.takeCountGenerationLimit ?? 0) -
      (data.user.productAccessPeriodUsage.clipsGenerated +
        data.user.productAccessPeriodUsage.takesGenerated),
    0
  );

  // Trial has ended if the user has no more clips left or they reached the trial duration limit
  const trialEnded =
    takesRemaining <= 0 ||
    (timeRemainingInMs && timeRemainingInMs < 0) ||
    subscriptionData?.user.subscription?.status ===
      SubscriptionStatus.TrialExpired;

  return (
    <UpgradeBanner
      ref={ref}
      {...{
        trialEnded,
        takesRemaining,
        timeLeft: humanizedTimeLeft,
        onClick,
        style,
        showUpgradeButton: !subscriptionData?.user.subscription?.plan.managed,
        isBusinessTrial: Boolean(
          subscriptionData?.user.subscription?.plan.displayName
            .toLowerCase()
            .includes('business')
        ),
      }}
    />
  );
});

export default UpgradeBannerWrapper;
